import {loadStripe} from '@stripe/stripe-js';
import {getSetting} from '@woocommerce/settings'
import apiFetch from "@wordpress/api-fetch";
import {getCurrency, formatPrice as wcFormatPrice} from '@woocommerce/price-format';

const {publishableKey, stripeParams} = getSetting('stripeGeneralData');
const messages = getSetting('stripeErrorMessages');
const countryLocale = getSetting('countryLocale', {});

const SHIPPING_OPTION_REGEX = /^([\w]+)\:(.+)$/;

const routes = getSetting('stripeGeneralData').routes;

const creditCardForms = {};

const localPaymentMethods = [];

const CACHE_PREFIX = 'stripe:';

const PAYMENT_REQUEST_ADDRESS_MAPPINGS = {
    recipient: (address, name) => {
        address.first_name = name.split(' ').slice(0, -1).join(' ');
        address.last_name = name.split(' ').pop();
        return address;
    },
    payerName: (address, name) => {
        address.first_name = name.split(' ').slice(0, -1).join(' ');
        address.last_name = name.split(' ').pop();
        return address;
    },
    name: (address, name) => {
        address.first_name = name.split(' ').slice(0, -1).join(' ');
        address.last_name = name.split(' ').pop();
        return address;
    },
    country: 'country',
    addressLine: (address, value) => {
        if (value[0]) {
            address.address_1 = value[0];
        }
        if (value[1]) {
            address.address_2 = value[1];
        }
        return address;
    },
    line1: 'address_1',
    line2: 'address_2',
    city: 'city',
    region: 'state',
    state: 'state',
    postalCode: 'postcode',
    postal_code: 'postcode',
    payerEmail: 'email',
    payerPhone: 'phone'
}

export const initStripe = new Promise((resolve, reject) => {
    loadStripe(publishableKey, stripeParams).then(stripe => {
        resolve(stripe);
    }).catch(err => {
        // Resolve null so Stripe's React components treat it as "no Stripe" instead of an invalid prop.
        console.error('Stripe.js failed to load.', err);
        resolve(null);
    });
});

export const registerCreditCardForm = ({id, ...props}) => {
    creditCardForms[id] = props;
}

export const getCreditCardForm = (id) => {
    return creditCardForms.hasOwnProperty(id) ? creditCardForms[id] : {};
}

export const getRoute = (route) => {
    return routes?.[route] ? routes[route] : console.log(`${route} is not a valid route`);
}

export const ensureSuccessResponse = (responseTypes, data = {}) => {
    return {type: responseTypes.SUCCESS, ...data};
}

/**
 * Returns a formatted error object used by observers
 * @param responseTypes
 * @param error
 * @returns {{type: *, message: *}}
 */
export const ensureErrorResponse = (responseTypes, error, options = {}) => {
    return {type: responseTypes.ERROR, message: getErrorMessage(error), ...options}
};

/**
 * Return a customized error message.
 * @param error
 */
export const getErrorMessage = (error) => {
    if (typeof error == 'string') {
        return error;
    }
    if (error?.code && messages?.[error.code]) {
        return messages[error.code];
    }
    if (error?.statusCode) {
        return messages?.[error.statusCode] ? messages[error.statusCode] : error.statusMessage;
    }
    return error.message;
}

/**
 * Return a Stripe formatted billing_details object from a WC address
 * @param billingAddress
 */
export const getBillingDetailsFromAddress = (billingAddress) => {
    let billing_details = {
        name: `${billingAddress.first_name} ${billingAddress.last_name}`,
        address: {
            city: billingAddress.city || null,
            country: billingAddress.country || null,
            line1: billingAddress.address_1 || null,
            line2: billingAddress.address_2 || null,
            postal_code: billingAddress.postcode || null,
            state: billingAddress.state || null
        }
    }
    if (billingAddress?.phone) {
        billing_details.phone = billingAddress.phone;
    }
    if (billingAddress?.email) {
        billing_details.email = billingAddress.email;
    }
    return billing_details;
}

export const getSettings = (name) => (key) => {
    if (key) {
        return getSetting(name)[key];
    }
    return getSetting(name);
}

export class StripeError extends Error {
    constructor(error) {
        super(error.message);
        this.error = error;
    }
}

/**
 * Returns true if the provided value is empty.
 * @param value
 * @returns {boolean}
 */
export const isEmpty = (value) => {
    if (typeof value === 'string') {
        return value.length == 0 || value == '';
    }
    if (Array.isArray(value)) {
        return array.length == 0;
    }
    if (typeof value === 'object') {
        return Object.keys(value).length == 0;
    }
    if (typeof value === 'undefined') {
        return true;
    }
    return true;
}

export const removeNumberPrecision = (value, unit) => {
    return value / 10 ** unit;
}

/**
 * Rescales a WC Blocks cart amount (expressed in WooCommerce's own configured minor
 * unit) to the minor unit Stripe expects for the given currency. The two can differ
 * because WooCommerce's Decimals setting is merchant-configurable per currency while
 * Stripe's minor unit is fixed (see stripeGeneralData.currencyMinorUnits, sourced from
 * wc_stripe_get_currencies()). A no-op when the two already match.
 *
 * @param {number} value    - Amount in WC Blocks' minor unit (e.g. cartTotal.value).
 * @param {Object} currency - WC Blocks currency object ({code, minorUnit, ...}).
 * @return {number}
 */
export const getStripeAmount = (value, currency) => {
    const wcMinorUnit = currency?.minorUnit ?? 2;
    const stripeMinorUnit = getSetting('stripeGeneralData').currencyMinorUnits?.[currency?.code] ?? 2;
    return Math.round(parseInt(value) * 10 ** (stripeMinorUnit - wcMinorUnit));
}

/**
 *
 * @param address
 * @param country
 */
export const isAddressValid = (address, exclude = []) => {
    const fields = getLocaleFields(address.country);
    for (const [key, value] of Object.entries(address)) {
        if (!exclude.includes(key) && fields?.[key] && fields[key].required) {
            if (isEmpty(value)) {
                return false;
            }
        }
    }
    return true;
}

export const getLocaleFields = (country) => {
    let localeFields = {...countryLocale.default};
    if (country && countryLocale?.[country]) {
        localeFields = Object.entries(countryLocale[country]).reduce((locale, [key, value]) => {
            locale[key] = {...locale[key], ...value}
            return locale;
        }, localeFields);
        ['phone', 'shipping-phone', 'email'].forEach(key => {
            let node = document.getElementById(key);
            if (node) {
                localeFields[key] = {required: node.required};
            }
        });
    }
    return localeFields;
}

/**
 * Return true if the field is required by the cart
 * @param field
 * @param defaultValue
 * @returns {boolean|*}
 */
export const isFieldRequired = (field, defaultValue = false) => {
    const fields = getSetting('defaultFields');
    if (fields && fields.hasOwnProperty(field)) {
        return !!fields[field]?.required;
    }
    return defaultValue;
}

export const getSelectedShippingOption = (id) => {
    const result = id.match(SHIPPING_OPTION_REGEX);
    if (result) {
        const {1: packageIdx, 2: rate} = result;
        return [rate, packageIdx];
    }
    return [];
}

export const hasShippingRates = (shippingRates) => {
    return shippingRates.map(rate => {
        return rate.shipping_rates.length > 0;
    }).filter(Boolean).length > 0;
}

/**
 * Return true if the customer is logged in.
 * @param customerId
 * @returns {boolean}
 */
export const isUserLoggedIn = (customerId) => {
    return customerId > 0;
}

const syncPaymentIntentWithOrder = async (order_id, client_secret) => {
    try {
        await apiFetch({
            url: routes['sync/intent'],
            method: 'POST',
            data: {order_id, client_secret}
        })
    } catch (error) {
        console.log(error);
    }
}

export const handleCardAction = async (
    {
        args,
        stripe,
        redirectUrl,
        emitResponse,
    }) => {
    try {
        let {
            type,
            status,
            return_url,
            client_secret
        } = args;
        if (['requires_action', 'requires_payment_method', 'requires_confirmation'].includes(status)) {
            let result;
            if (type === 'payment_intent') {
                result = await stripe.confirmCardPayment(client_secret);
            } else {
                result = await stripe.confirmCardSetup(client_secret);
            }
            if (result.error) {
                throw result.error;
            }

            const url = new URL(return_url);
            url.searchParams.append('payment_intent', result.paymentIntent.id);
            url.searchParams.append('payment_intent_client_secret', result.paymentIntent.client_secret);

            return ensureSuccessResponse(emitResponse.responseTypes, {
                redirectUrl: url.toString()
            });
        }
        return ensureSuccessResponse(emitResponse.responseTypes);
    } catch (err) {
        console.log(err);
        return ensureErrorResponse(
            emitResponse.responseTypes,
            err,
            {
                messageContext: emitResponse.noticeContexts.PAYMENTS
            }
        );
    }
}

export const handleNextAction = async (
    {
        args,
        stripe,
        elements = null,
        checkout = null,
        billingAddress
    }) => {
    // A checkout-session redirect (Adaptive Pricing) carries no `status` - the session itself is
    // the source of truth, confirmed via the CheckoutProvider's own checkout.confirm(), not
    // stripe.confirmPayment()/confirmSetup().
    if (args.type === 'checkout_session') {
        const billing = args.billing_details || {};
        const result = await checkout.confirm({
            redirect: 'if_required',
            ...(args.return_url && {returnUrl: args.return_url}),
            ...(args.payment_method && {paymentMethod: args.payment_method}),
            ...(billing.email && {email: billing.email}),
            ...(billing.phone && {phoneNumber: billing.phone}),
            ...(!args.payment_method && billing.address?.country && {
                billingAddress: {
                    address: billing.address,
                    ...(billing.name && {name: billing.name})
                }
            })
        });
        if (result.type === 'error') {
            throw result.error;
        }
        return {redirectUrl: args.return_url};
    }

    let {type, client_secret, status, return_url = null, confirmation_args = null} = args;
    if (['requires_action', 'requires_payment_method', 'requires_confirmation'].includes(status)) {
        let result;
        if (type === 'payment_intent') {
            result = await stripe.confirmPayment({
                ...(elements && {elements}),
                clientSecret: client_secret,
                redirect: 'if_required',
                confirmParams: {
                    return_url: return_url,
                    payment_method_data: {
                        billing_details: getBillingDetailsFromAddress(billingAddress)
                    },
                    expand: ['payment_method'],
                    ...(confirmation_args && confirmation_args)
                }
            });
        } else {
            result = await stripe.confirmSetup({
                clientSecret: client_secret,
                redirect: 'if_required',
                confirmParams: {
                    return_url: return_url,
                    payment_method_data: {
                        billing_details: getBillingDetailsFromAddress(billingAddress)
                    },
                    expand: ['payment_method']
                }
            });
        }
        if (result.error) {
            throw result.error;
        }

        // With redirect: 'if_required', Stripe returns the result instead of redirecting.
        // We need to handle statuses that indicate the payment did not succeed.
        if (type === 'payment_intent') {
            const status = result.paymentIntent?.status;
            const nextActionType = result.paymentIntent?.next_action?.type;
            if (status === 'requires_action' && nextActionType !== 'verify_with_microdeposits'
                && !nextActionType?.endsWith('_display_details')) {
                throw {code: 'payment_cancelled'};
            }
            if (status === 'requires_payment_method') {
                throw {
                    code: result.paymentIntent?.last_payment_error?.code,
                    message: result.paymentIntent?.last_payment_error?.message,
                };
            }
        } else {
            const status = result.setupIntent?.status;
            if (status === 'requires_action') {
                throw {code: 'payment_cancelled'};
            }
            if (status === 'requires_payment_method') {
                // Setup intents use last_setup_error, not last_payment_error
                throw {
                    code: result.setupIntent?.last_setup_error?.code,
                    message: result.setupIntent?.last_setup_error?.message,
                };
            }
        }

        const url = new URL(return_url);
        if (type === 'payment_intent') {
            url.searchParams.append('payment_intent', result.paymentIntent.id);
            url.searchParams.append('payment_intent_client_secret', result.paymentIntent.client_secret);
        } else {
            url.searchParams.append('setup_intent', result.setupIntent.id);
            url.searchParams.append('setup_intent_client_secret', result.setupIntent.client_secret);
        }
        return {redirectUrl: url.toString()};
    }
}

/**
 * Convert a payment wallet address to a WC cart address.
 * @param address_mappings
 * @returns {function(*, *=): {}}
 */
export const toCartAddress = (address_mappings = PAYMENT_REQUEST_ADDRESS_MAPPINGS) => (address, args = {}) => {
    const cartAddress = {};
    address = {...address, ...filterEmptyValues(args)};
    for (let [key, cartKey] of Object.entries(address_mappings)) {
        if (address?.[key]) {
            if (typeof cartKey === 'function') {
                cartKey(cartAddress, address[key]);
            } else {
                cartAddress[cartKey] = address[key];
            }
        }
    }
    return cartAddress;
}

/**
 * Given a WC formatted address, return only the intermediate address values
 * @param address
 * @param fields
 */
export const getIntermediateAddress = (address, fields = ['city', 'postcode', 'state', 'country']) => {
    const intermediateAddress = {};
    for (let key of fields) {
        intermediateAddress[key] = address[key];
    }
    return intermediateAddress;
}

/**
 *
 * @param values
 * @returns {{}|{[p: string]: *}}
 */
export const filterEmptyValues = (values) => {
    return Object.keys(values).filter(key => Boolean(values[key])).reduce((obj, key) => ({
        ...obj,
        [key]: values[key]
    }), {});
}

export const formatPrice = (price, currencyCode) => {
    const {prefix, suffix, decimalSeparator, minorUnit, thousandSeparator} = getCurrency(currencyCode);
    if (price == '' || price === undefined) {
        return price;
    }

    price = typeof price === 'string' ? parseInt(price, 10) : price;
    price = price / 10 ** minorUnit;
    price = price.toString().replace('.', decimalSeparator);
    let fractional = '';
    const index = price.indexOf(decimalSeparator);
    if (index < 0) {
        if (minorUnit > 0) {
            price += `${decimalSeparator}${new Array(minorUnit + 1).join('0')}`;
        }
    } else {
        fractional = price.substr(index + 1);
        if (fractional.length < minorUnit) {
            price += new Array(minorUnit - fractional.length + 1).join('0');
        }
    }

    // separate out price and decimals so thousands separator can be added.
    const match = price.match(new RegExp(`(\\d+)\\${decimalSeparator}(\\d+)`));
    if (match) {
        ({1: price, 2: fractional} = match);
    }
    price = price.replace(new RegExp(`\\B(?=(\\d{3})+(?!\\d))`, 'g'), `${thousandSeparator}`);
    price = fractional?.length > 0 ? price + decimalSeparator + fractional : price;
    price = prefix + price + suffix;
    return price;
}

export const getShippingOptions = (shippingRates, options = {label: 'label'}) => {
    let shippingOptions = [];
    const selectedIds = [];
    shippingRates.forEach((shippingPackage, idx) => {
        // Selected rate first - Stripe shows the first entry as the default option.
        const rates = [...shippingPackage.shipping_rates].sort(
            (a, b) => (b.selected === true) - (a.selected === true)
        );
        rates.forEach(rate => {
            const id = getShippingOptionId(idx, rate.rate_id);
            if (rate.selected === true) {
                selectedIds.push(id);
            }
            const txt = document.createElement('textarea');
            txt.innerHTML = rate.name;
            shippingOptions.push({
                id,
                [options.label]: txt.value,
                amount: parseInt(rate.price, 10)
            });
        });
    });

    // Stripe's Express Checkout Element rejects a shippingRates array with 10 or more
    // entries ("shippingRates should be an array of size less than 10"), which stalls the
    // wallet payment sheet. Cap at 9, always keeping the selected rate(s) even when they
    // sort past the cap, so the chosen method still matches an offered option.
    const MAX_SHIPPING_OPTIONS = 9;
    if (shippingOptions.length > MAX_SHIPPING_OPTIONS) {
        shippingOptions = [
            ...shippingOptions.filter(option => selectedIds.includes(option.id)),
            ...shippingOptions.filter(option => !selectedIds.includes(option.id))
        ].slice(0, MAX_SHIPPING_OPTIONS);
    }

    return shippingOptions;
}

export const getShippingOptionId = (packageId, rateId) => `${packageId}:${rateId}`

export const getDisplayItems = (cartItems, {minorUnit}) => {
    let items = [];
    const keys = ['total_tax', 'total_shipping'];
    cartItems.forEach(item => {
        if (0 < item.value || (item.key && keys.includes(item.key))) {
            items.push({
                label: item.label,
                pending: false,
                amount: item.value
            });
        }
    })
    return items;
}

export const mapLineItems = items => {
    return items.map(item => ({
        name: item.label,
        amount: item.amountCents
    }))
}

export const registerLocalPaymentMethod = (paymentMethod) => {
    localPaymentMethods.push(paymentMethod);
}

export const getLocalPaymentMethods = () => localPaymentMethods;

export const getDefaultSourceArgs = ({type, amount, billingAddress, currency, returnUrl}) => {
    return {
        type,
        amount,
        currency,
        owner: getBillingDetailsFromAddress(billingAddress),
        redirect: {
            return_url: returnUrl
        }
    }
}

export const isTestMode = () => {
    return getSetting('stripeGeneralData').mode === 'test';
}

const getCacheKey = (key) => `${CACHE_PREFIX}${key}`;

export const storeInCache = (key, value) => {
    const exp = Math.floor(new Date().getTime() / 1000) + (60 * 15);
    if ('sessionStorage' in window) {
        sessionStorage.setItem(getCacheKey(key), JSON.stringify({value, exp}));
    }
}

export const getFromCache = (key) => {
    if ('sessionStorage' in window) {
        try {
            const item = JSON.parse(sessionStorage.getItem(getCacheKey(key)));
            if (item) {
                const {value, exp} = item;
                if (Math.floor(new Date().getTime() / 1000) > exp) {
                    deleteFromCache(getCacheKey(key));
                } else {
                    return value;
                }
            }
        } catch (err) {
        }
    }
    return null;
}

export const deleteFromCache = (key) => {
    if ('sessionStorage' in window) {
        sessionStorage.removeItem(getCacheKey(key));
    }
}

export const getPage = () => getSetting('stripeGeneralData').page;

export const isCartPage = () => getPage() === 'cart' && !getSetting('isCheckoutBlock');

export const isCheckoutPage = () => getPage() === 'checkout' || !!getSetting('isCheckoutBlock');

export const isNextActionRequired = (url) => {
    let match = url.match(/#response=(.+)/);
    let args = null;
    if (match) {
        args = JSON.parse(window.atob(decodeURIComponent(match[1])));
    }
    return args;
}

export const isKlarnaAvailable = ({eea_countries, accountCountry, requiredParams, currency, country = null}) => {
    let result = false;
    if (!country) {
        return currency in requiredParams;
    }

    const countries = [...eea_countries, 'GB', 'CH'];

    if (countries.includes(accountCountry)) {
        if (countries.includes(country)) {
            result = currency in requiredParams &&
                requiredParams[currency].includes(country);
        }
    } else {
        result = currency in requiredParams && requiredParams[currency].includes(country);
    }

    return result;
}

const PAYMENT_ELEMENT_TERMS_KEYS = [
    'applePay', 'auBecsDebit', 'bancontact', 'card', 'cashapp',
    'googlePay', 'ideal', 'paypal', 'sepaDebit', 'sofort', 'usBankAccount'
];

/**
 * Build the terms object for PaymentElement options.
 * Mirrors BaseGateway.getTerms() for the blocks implementation.
 *
 * @param {string} rule - 'auto' or 'never'
 * @returns {Object}
 */
export const getPaymentElementTerms = (rule) => {
    return PAYMENT_ELEMENT_TERMS_KEYS.reduce((terms, key) => {
        terms[key] = rule;
        return terms;
    }, {});
};

export const formatPaymentMethod = (format, data) => {
    // Prepare the replacement data with placeholders
    const replacementData = {};

    // Create placeholders from the data object
    Object.keys(data).forEach(key => {
        replacementData[`{${key}}`] = data[key];
    });

    // Replace all placeholders with actual values
    let result = format;
    Object.keys(replacementData).forEach(placeholder => {
        // Use split and join which is more efficient for simple replacements
        while (result.includes(placeholder)) {
            result = result.replace(placeholder, replacementData[placeholder]);
        }
    });

    return result;
}

export const ucfirst = (value) => {
    return value.charAt(0).toUpperCase() + value.slice(1)
}

export const DEFAULT_SHIPPING_ADDRESS = {
    'first_name': '',
    'last_name': '',
    'company': '',
    'address_1': '',
    'address_2': '',
    'city': '',
    'state': '',
    'postcode': '',
    'country': '',
    'phone': '',
}

export const DEFAULT_BILLING_ADDRESS = {
    ...DEFAULT_SHIPPING_ADDRESS,
    'email': ''
}